import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth, resolveOnboardingPath } from "../../../contexts/AuthContext";
import {
  getPaymentPlansByAccount,
  createSubscriptionIntent,
} from "../../../services/billing.service";
import StripeCheckoutModal from "../../../components/settings/billing/StripeCheckoutModal";
import OnboardingLogout from "../../../components/auth/OnboardingLogout";
import ChoiceChip from "../../../ui/selectors/ChoiceChip";
import styles from "../../../styles/pages/auth/SignupPlanPage.module.css";

const CURRENT_PATH = "/signup/plan";

const ACTIVATION_POLL_ATTEMPTS = 8;
const ACTIVATION_POLL_INTERVAL_MS = 1500;

const formatPrice = (amount) => `$${Number(amount || 0).toLocaleString("en-US")}`;

const resolveBillingAccountId = (billingAccount) =>
  billingAccount?.billing_account_id ?? billingAccount?.id ?? null;

export default function SignupPlanPage() {
  const navigate = useNavigate();
  const { auth, refreshMe, refreshAccounts } = useAuth();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [billingType, setBillingType] = useState("monthly");
  const [loadingPlanCode, setLoadingPlanCode] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState("");
  const [activating, setActivating] = useState(false);

  const scrollerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const dragStartXRef = useRef(0);
  const scrollStartLeftRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const account = useMemo(() => {
    const pending = (auth?.billingAccounts || []).find(
      (item) =>
        item?.can_manage_billing && item?.action_required === "select_plan",
    );
    if (!pending) return null;
    return {
      id: resolveBillingAccountId(pending),
      name: pending.billing_account_name ?? pending.name,
    };
  }, [auth]);

  useEffect(() => {
    const target = resolveOnboardingPath(auth);
    if (target !== CURRENT_PATH) {
      navigate(target || "/dashboard", { replace: true });
    }
  }, [auth, navigate]);

  useEffect(() => {
    if (!account?.id) return undefined;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getPaymentPlansByAccount(account.id);
        if (!active) return;
        setPlans(Array.isArray(response?.plans) ? response.plans : []);
      } catch (fetchError) {
        if (!active) return;
        setError("No se pudieron cargar los planes.");
        console.error(fetchError?.response?.data || fetchError);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [account?.id]);

  // Drag-to-scroll horizontal de la grilla de planes.
  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!isDraggingRef.current || !scrollerRef.current) return;
      const deltaX = event.clientX - dragStartXRef.current;
      if (Math.abs(deltaX) > 3) hasDraggedRef.current = true;
      scrollerRef.current.scrollLeft = scrollStartLeftRef.current - deltaX;
    };
    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      if (scrollerRef.current) scrollerRef.current.style.cursor = "grab";
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleScrollerMouseDown = (event) => {
    if (!scrollerRef.current) return;
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = event.clientX;
    scrollStartLeftRef.current = scrollerRef.current.scrollLeft;
    scrollerRef.current.style.cursor = "grabbing";
  };

  const handleScrollerClickCapture = (event) => {
    if (hasDraggedRef.current) {
      event.stopPropagation();
      hasDraggedRef.current = false;
    }
  };

  const scrollByColumn = (direction) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: direction * 260, behavior: "smooth" });
  };

  const isAnnual = billingType === "annual";

  const featureLabels = useMemo(() => {
    const withFeatures = plans.find(
      (plan) => Array.isArray(plan.features) && plan.features.length,
    );
    return (withFeatures?.features || []).map((feature) => feature.label);
  }, [plans]);

  const isPlanFree = (plan) => Number(plan.price_monthly || 0) === 0;

  const getBadge = (plan) => {
    if (isPlanFree(plan)) return "Gratis";
    const savings =
      Number(plan.price_monthly || 0) * 12 - Number(plan.price_annual || 0);
    return savings > 0 ? `Ahorro: ${formatPrice(savings)}/año` : "";
  };

  // El plan lo activa un webhook tras pagar; reintentamos /me hasta verlo activo.
  const waitForActivation = async () => {
    if (mountedRef.current) setActivating(true);
    try {
      for (let attempt = 0; attempt < ACTIVATION_POLL_ATTEMPTS; attempt++) {
        const nextAuth = await refreshMe();
        if (!mountedRef.current) return; // desmontado (ej. redirigió al activarse)
        const stillNeedsPlan = (nextAuth?.billingAccounts || []).some(
          (item) =>
            resolveBillingAccountId(item) === account?.id &&
            item?.action_required === "select_plan",
        );
        if (!stillNeedsPlan) {
          await refreshAccounts();
          return;
        }

        await new Promise((resolve) =>
          setTimeout(resolve, ACTIVATION_POLL_INTERVAL_MS),
        );
        if (!mountedRef.current) return;
      }

      await refreshAccounts();
      if (mountedRef.current) {
        setError(
          "Tu pago se está procesando. Si en unos segundos no se activa, recargá la página.",
        );
      }
    } finally {
      if (mountedRef.current) setActivating(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (!account?.id || loadingPlanCode) return;
    setError("");
    setLoadingPlanCode(plan.code);
    try {
      const result = await createSubscriptionIntent({
        account_id: account.id,
        plan_code: plan.code,
        billing_interval: billingType,
      });

      if (result?.client_secret) {
        setCheckoutClientSecret(result.client_secret);
        setCheckoutOpen(true);
        return;
      }

      if (result?.no_payment_required) {
        // Plan gratuito: ya quedó activo en el back; refrescar y salir.
        await refreshAccounts();
        await refreshMe();
        return;
      }

      // Respuesta inesperada (sin client_secret ni no_payment_required).
      setError("No se pudo activar el plan. Intentá nuevamente.");
    } catch (selectError) {
      setError(selectError.message || "No se pudo activar el plan.");
      console.error(selectError);
    } finally {
      setLoadingPlanCode(null);
    }
  };

  if (!account) return null;

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <OnboardingLogout />
      </div>

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Activá tu cuenta eligiendo un plan</h1>
          <p className={styles.subtitle}>
            Tu cuenta <strong>{account.name}</strong> necesita un plan activo
            para empezar a usar Maqmailing.
          </p>
        </div>

        <div className={styles.controls}>
          <div className={styles.billingToggle}>
            <ChoiceChip
              label="Mensual"
              selected={!isAnnual}
              onClick={() => setBillingType("monthly")}
            />
            <ChoiceChip
              label="Anual"
              selected={isAnnual}
              onClick={() => setBillingType("annual")}
            />
          </div>

          <div className={styles.arrows}>
            <button
              type="button"
              className={styles.arrowButton}
              aria-label="Anterior"
              onClick={() => scrollByColumn(-1)}
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.arrowButton}
              aria-label="Siguiente"
              onClick={() => scrollByColumn(1)}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {activating && (
        <div className={styles.activatingBanner}>
          Activando tu plan… esto puede tardar unos segundos.
        </div>
      )}

      {loading ? (
        <div className={styles.stateBox}>Cargando planes...</div>
      ) : (
        <div
          ref={scrollerRef}
          className={styles.scroller}
          onMouseDown={handleScrollerMouseDown}
          onClickCapture={handleScrollerClickCapture}
          style={{ cursor: "grab" }}
        >
          {featureLabels.length > 0 && (
            <div className={styles.featuresColumn}>
              <div className={styles.featuresColumnHeader} />
              <div className={styles.featuresColumnBody}>
                {featureLabels.map((label) => (
                  <div key={label} className={styles.featureLabelRow}>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {plans.map((plan) => {
            const free = isPlanFree(plan);
            const badge = getBadge(plan);
            return (
              <div
                key={plan.code}
                className={`${styles.planColumn} ${free ? styles.planColumnFeatured : ""}`}
              >
                <div className={styles.planHeader}>
                  {badge ? <div className={styles.badge}>{badge}</div> : null}

                  <div className={styles.planName}>{plan.name}</div>

                  <div className={styles.planPriceBlock}>
                    <div className={styles.planPrice}>
                      {plan.is_enterprise ? (
                        "Pedir Presupuesto"
                      ) : (
                        <>
                          {formatPrice(
                            isAnnual ? plan.price_annual : plan.price_monthly,
                          )}{" "}
                          <span className={styles.planCurrency}>
                            {(plan.currency || "USD").toUpperCase()}
                          </span>
                          <span className={styles.planPriceSuffix}>
                            {isAnnual ? "/año" : "/mes"}
                          </span>
                        </>
                      )}
                    </div>
                    {/* Línea reservada para alinear el CTA en todas las cards. */}
                    <div className={styles.planBillingText}>
                      {!isAnnual && Number(plan.price_annual || 0) > 0
                        ? `Facturado como ${formatPrice(plan.price_annual)} anual`
                        : " "}
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`${styles.ctaButton} ${free ? styles.ctaButtonFeatured : ""}`}
                    onClick={() => handleSelectPlan(plan)}
                    disabled={!!loadingPlanCode || activating}
                  >
                    {loadingPlanCode === plan.code
                      ? "Procesando..."
                      : free
                        ? "Empieza gratis"
                        : "Empieza ya"}
                  </button>
                </div>

                {featureLabels.length > 0 && (
                  <div className={styles.planFeaturesBody}>
                    {featureLabels.map((label) => {
                      const feature = (plan.features || []).find(
                        (item) => item.label === label,
                      );
                      return (
                        <div key={label} className={styles.featureValueRow}>
                          {feature?.value ?? "–"}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <StripeCheckoutModal
        open={checkoutOpen}
        title="Completar suscripción"
        clientSecret={checkoutClientSecret}
        onClose={() => setCheckoutOpen(false)}
        onDone={async () => {
          setCheckoutOpen(false);
          setCheckoutClientSecret("");
          await waitForActivation();
        }}
      />
    </div>
  );
}
